const FETCH_GUARD_FLAG = '__livewire419ReloadGuardInstalled';
const RELOAD_STATE_KEY = 'livewire_419_reload_state';
const LOGOUT_FORM_LOCK_FLAG = '__logoutFormSubmitLockInstalled';
const LIVEWIRE_419_HOOK_FLAG = '__livewire419HookInstalled';
const LIVEWIRE_ROOT_UPDATES_HOOK_FLAG = '__livewireRootUpdatesHookInstalled';
const RELOAD_WINDOW_MS = 2 * 60 * 1000;
const MAX_RELOADS_PER_WINDOW = 2;

export function installLivewireGuard() {
    installLogoutFormSubmitLock();
    installLivewire419Handler();
    installLivewireRootUpdatesHandler();

    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return;
    }

    if (window[FETCH_GUARD_FLAG] === true) {
        return;
    }

    const originalFetch = window.fetch.bind(window);

    window.fetch = async function (input, init) {
        const response = await originalFetch(input, init);

        try {
            const requestUrl = typeof input === 'string' ? input : input?.url;
            const isLivewireUpdate = typeof requestUrl === 'string' && requestUrl.includes('/livewire/update');
            const shouldReload = isLivewireUpdate && response.status === 419;

            if (shouldReload) {
                reloadOnceAfter419();
            } else if (response.ok) {
                clearReloadState();
            }
        } catch (e) {
            // Never block the original fetch flow.
        }

        return response;
    };

    window[FETCH_GUARD_FLAG] = true;
}

export function normalizeLivewireRootUpdates(component, commit) {
    const updates = commit.updates;
    const rootUpdate = updates?.[''];

    if (!rootUpdate || typeof rootUpdate !== 'object' || Array.isArray(rootUpdate)) {
        return;
    }

    // Livewire 3.8.2 can mistake reordered root keys for a whole-component update.
    delete updates[''];

    const appendChanges = (path, previous, value) => {
        if (Object.hasOwn(updates, path) || JSON.stringify(previous) === JSON.stringify(value)) {
            return;
        }

        const hasNestedUpdate = Object.keys(updates).some((key) => key.startsWith(`${path}.`));

        if (hasNestedUpdate && value && typeof value === 'object') {
            for (const [key, child] of Object.entries(value)) {
                appendChanges(`${path}.${key}`, previous?.[key], child);
            }
            for (const key of Object.keys(previous ?? {})) {
                if (!Object.hasOwn(value, key)) {
                    appendChanges(`${path}.${key}`, previous[key], '__rm__');
                }
            }
            return;
        }

        if (!hasNestedUpdate) {
            updates[path] = value;
        }
    };

    for (const [property, value] of Object.entries(rootUpdate)) {
        if (property !== '') {
            appendChanges(property, component.canonical[property], value);
        }
    }
}

function installLivewireRootUpdatesHandler() {
    const registerHandler = () => {
        if (window[LIVEWIRE_ROOT_UPDATES_HOOK_FLAG] || typeof window.Livewire?.hook !== 'function') {
            return;
        }

        window.Livewire.hook('commit', ({ component, commit }) => {
            normalizeLivewireRootUpdates(component, commit);
        });
        window[LIVEWIRE_ROOT_UPDATES_HOOK_FLAG] = true;
    };

    registerHandler();
    document.addEventListener('livewire:initialized', registerHandler, { once: true });
}

function installLivewire419Handler() {
    if (typeof window === 'undefined' || window[LIVEWIRE_419_HOOK_FLAG] === true) {
        return;
    }

    const registerHandler = () => {
        if (typeof window.Livewire === 'undefined' || typeof window.Livewire.hook !== 'function') {
            return;
        }

        window.Livewire.hook('request', ({ fail }) => {
            fail(({ status, preventDefault }) => {
                if (status !== 419) {
                    return;
                }

                if (typeof preventDefault === 'function') {
                    preventDefault();
                }

                reloadOnceAfter419();
            });
        });

        window[LIVEWIRE_419_HOOK_FLAG] = true;
    };

    registerHandler();
    document.addEventListener('livewire:initialized', registerHandler, { once: true });
}

function reloadOnceAfter419() {
    const state = getReloadState();
    const now = Date.now();
    const isWindowExpired = now - state.lastReloadAt > RELOAD_WINDOW_MS;

    if (isWindowExpired) {
        state.count = 0;
    }

    if (state.count >= MAX_RELOADS_PER_WINDOW) {
        return;
    }

    state.count += 1;
    state.lastReloadAt = now;
    sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state));
    window.location.reload();
}

function getReloadState() {
    try {
        const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;

        if (
            parsed
            && Number.isInteger(parsed.count)
            && Number.isInteger(parsed.lastReloadAt)
        ) {
            return parsed;
        }
    } catch (e) {
        // Ignore malformed state and return defaults below.
    }

    return {
        count: 0,
        lastReloadAt: 0,
    };
}

function clearReloadState() {
    sessionStorage.removeItem(RELOAD_STATE_KEY);
}

function installLogoutFormSubmitLock() {
    if (window[LOGOUT_FORM_LOCK_FLAG] === true || typeof document === 'undefined') {
        return;
    }

    document.addEventListener('submit', (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        const action = form.getAttribute('action') || '';
        const isLogoutForm = action.endsWith('/portal/logout');

        if (!isLogoutForm) {
            return;
        }

        if (form.dataset.logoutSubmitting === '1') {
            event.preventDefault();
            return;
        }

        form.dataset.logoutSubmitting = '1';

        const submitter = event.submitter;
        if (submitter && 'disabled' in submitter) {
            submitter.disabled = true;
        }
    }, true);

    window[LOGOUT_FORM_LOCK_FLAG] = true;
}
